/**
 * useIntroVideo Hook
 *
 * Manages the intro video display state for Electron packaged app.
 * The intro video is only shown on first launch in packaged Electron app.
 *
 * Conditions for showing intro:
 * - Not in development mode (import.meta.env.DEV)
 * - Running in Electron (isElectron)
 * - electronAPI is available
 * - VITE_SATCHEL_SKIP_INTRO env var is not set
 * - introSeen is not true in app config
 */

import { useState, useEffect, useCallback } from "react";
import { isElectron } from "../utils/runtime";
import { isDevelopmentMode, shouldSkipIntro } from "../utils/env";

export interface UseIntroVideoOptions {
  /** Skip the intro check (useful for testing) */
  skip?: boolean;
}

export interface UseIntroVideoResult {
  /** Whether the intro video should be shown */
  showIntroVideo: boolean;
  /** Handler to call when intro video is completed/dismissed */
  handleIntroComplete: () => void;
  /** Whether the intro check has finished */
  isReady: boolean;
}

/**
 * Hook to manage intro video display state
 */
export function useIntroVideo(options: UseIntroVideoOptions = {}): UseIntroVideoResult {
  const { skip = false } = options;
  const [showIntroVideo, setShowIntroVideo] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (skip) {
      setIsReady(true);
      return;
    }

    const checkIntroStatus = async () => {
      // Skip in development mode - only show in packaged app
      if (isDevelopmentMode()) {
        setIsReady(true);
        return;
      }

      // Only show intro on Electron
      if (!isElectron || !window.electronAPI) {
        setIsReady(true);
        return;
      }

      // Check env override to skip intro
      if (shouldSkipIntro()) {
        setIsReady(true);
        return;
      }

      try {
        // Check if intro has been seen before
        const appConfig = await window.electronAPI.getAppConfig();
        if (appConfig.introSeen) {
          setIsReady(true);
          return;
        }

        // Show intro video (bundled via Vite import in StartupVideo)
        setShowIntroVideo(true);
        setIsReady(true);
      } catch (error) {
        console.error("Failed to check intro status:", error);
        setIsReady(true);
      }
    };

    checkIntroStatus();
  }, [skip]);

  const handleIntroComplete = useCallback(() => {
    setShowIntroVideo(false);
  }, []);

  return {
    showIntroVideo,
    handleIntroComplete,
    isReady,
  };
}
